import { Box, Button, Icon, Typography, useTheme } from "@outschool/backpack";
import {
  faBoltLightning,
  faPencil,
  faThumbsDown,
  faThumbsUp,
  faWandMagicSparkles,
  fasThumbsDown,
  fasThumbsUp
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { IconButton, TextArea } from "@outschool/ui-legacy-component-library";
import React from "react";

export default function TeacherNoteGenerater({
  sectionUid,
  teacherNotes,
  generatedLongNote,
  generatedShortNote,
  sendProgressReportMessage,
  isSendingProgressReport,
  isGeneratingProgressReport,
  isTablet,
  textAreaHeight
}: {
  sectionUid: string;
  teacherNotes: string | undefined;
  generatedLongNote: string | undefined;
  generatedShortNote: string | undefined;
  sendProgressReportMessage: (
    providedMessage: string | undefined,
    reportLength: string,
    generatedReport: boolean,
    changedGeneratedReport: boolean
  ) => void;
  isSendingProgressReport: boolean | undefined;
  isGeneratingProgressReport: boolean;
  isTablet: boolean;
  textAreaHeight?: number;
}) {
  const { t } = useTranslation("client\\components\\TeacherNoteGenerater");
  const trackEvent = useTrackEvent();
  const theme = useTheme();
  const [note, setNote] = React.useState<string | undefined>(generatedLongNote);
  const [aiUsed, setAiUsed] = React.useState<string>("FewParagraphs");
  const [thumbClicked, setThumbClicked] = React.useState<string | undefined>();
  const [generatedReportChanged, setGeneratedReportChanged] =
    React.useState<boolean>(false);

  return (
    <>
      <Box
        flex
        sx={{
          flexDirection: "row"
        }}
      >
        <Icon
          icon={faWandMagicSparkles}
          sx={{ marginRight: "0.5em", alignSelf: "center" }}
        />
        <Typography variant="h5">
          {t("AI Teaching Assistant suggested...")}
        </Typography>
      </Box>
      <Box
        flex
        sx={{
          flexDirection: "row",
          my: "1rem"
        }}
      >
        <Button
          variant={aiUsed == "FewParagraphs" ? "contained" : "outlined"}
          sx={{
            height: 40,
            marginRight: "1.5em",
            backgroundColor:
              aiUsed == "FewParagraphs"
                ? theme.palette.neutral[700]
                : theme.palette.common.white,
            borderColor:
              aiUsed == "FewParagraphs"
                ? theme.palette.neutral[700]
                : theme.palette.neutral[200],
            "&:hover:not(:disabled), &:focus:not(:disabled)": {
              backgroundColor:
                aiUsed == "FewParagraphs"
                  ? theme.palette.neutral[800]
                  : theme.palette.neutral[200],
              borderColor:
                aiUsed == "FewParagraphs"
                  ? theme.palette.neutral[800]
                  : theme.palette.neutral[300]
            },
            color: aiUsed == "OneParagraph" && theme.palette.neutral[900]
          }}
          onClick={async () => {
            setAiUsed("FewParagraphs");
            setNote(generatedLongNote);
          }}
          disabled={isGeneratingProgressReport}
        >
          <Icon icon={faPencil} sx={{ marginRight: "0.5em" }} />
          {t("Descriptive")}
        </Button>
        <Button
          variant={aiUsed == "OneParagraph" ? "contained" : "outlined"}
          sx={{
            height: 40,
            backgroundColor:
              aiUsed == "OneParagraph"
                ? theme.palette.neutral[700]
                : theme.palette.common.white,
            borderColor:
              aiUsed == "OneParagraph"
                ? theme.palette.neutral[700]
                : theme.palette.neutral[200],
            "&:hover:not(:disabled), &:focus:not(:disabled)": {
              backgroundColor:
                aiUsed == "OneParagraph"
                  ? theme.palette.neutral[800]
                  : theme.palette.neutral[200],
              borderColor:
                aiUsed == "OneParagraph"
                  ? theme.palette.neutral[800]
                  : theme.palette.neutral[300]
            },
            color: aiUsed == "FewParagraphs" && theme.palette.neutral[900]
          }}
          onClick={async () => {
            setAiUsed("OneParagraph");
            setNote(generatedShortNote);
          }}
          disabled={isGeneratingProgressReport}
        >
          <Icon icon={faBoltLightning} sx={{ marginRight: "0.5em" }} />
          {t("Concise")}
        </Button>
      </Box>
      <Box
        sx={{
          width: "100%"
        }}
      >
        <TextArea
          sx={{ width: "100%", minHeight: textAreaHeight ?? "12em" }}
          value={note}
          onChange={async (e: React.FormEvent<HTMLTextAreaElement>) => {
            setGeneratedReportChanged(true);
            setNote((e.target as HTMLTextAreaElement).value);
          }}
          showCount={false}
        />
      </Box>
      <Box
        flex
        sx={(theme: $TSFixMe) => ({
          flexDirection: isTablet ? "column" : "row",
          justifyContent: "space-between",

          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            gap: "0.5rem"
          }
        })}
      >
        <Button
          variant="contained"
          onClick={async () => {
            await sendProgressReportMessage(
              note,
              aiUsed,
              true,
              generatedReportChanged
            );
          }}
          disabled={isSendingProgressReport}
        >
          {t("Send Note to Parent")}
        </Button>
        <Box
          flex
          sx={{
            flexDirection: "row",
            justifyContent: isTablet ? "start" : "space-between"
          }}
        >
          <Typography
            variant="body1"
            sx={{
              alignSelf: "center"
            }}
          >
            {t("Is this helpful?")}
          </Typography>
          <IconButton
            sx={{
              color: "#368139",
              "&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)":
                {
                  backgroundColor: "#DDF0DC",
                  borderRadius: "99px",
                  color: "#368139"
                }
            }}
            type="button"
            icon={thumbClicked === "Up" ? fasThumbsUp : faThumbsUp}
            onClick={() => {
              setThumbClicked("Up");
              trackEvent("Note maker thumbs up clicked", {
                sectionUid,
                teacherNotes,
                generatedLongNote,
                generatedShortNote,
                aiUsed,
                generatedReportChanged
              });
            }}
            screenReaderLabel={t("Yes")}
            iconProps={{
              size: "1em"
            }}
          />
          <IconButton
            sx={{
              color: "#8F1104",
              "&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)":
                {
                  backgroundColor: "#FFE5DD",
                  borderRadius: "99px",
                  color: "#8F1104"
                }
            }}
            type="button"
            icon={thumbClicked === "Down" ? fasThumbsDown : faThumbsDown}
            onClick={() => {
              setThumbClicked("Down");
              trackEvent("Note maker thumbs down clicked", {
                sectionUid,
                teacherNotes,
                generatedLongNote,
                generatedShortNote,
                aiUsed,
                generatedReportChanged
              });
            }}
            screenReaderLabel={t("No")}
            iconProps={{
              size: "1em"
            }}
          />
        </Box>
      </Box>
    </>
  );
}
